import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableFooter } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import MoneyInput from '../components/MoneyInput'
import {
  draftRequested, investorTableRows, percentDone, requestedFromPercent, reviewOf, rowFigures, type DraftAmount, type DraftReview, type RowsMode,
} from '../lib/applications'
import { compareMoneyStrings, sumMoneyStrings } from '../lib/contracts'
import { formatMoney, toEditText } from '../lib/money'
import { collapsibleIds, normalizeSearch } from '../lib/tree'
import type { CostApplicationRow, CostApplicationStatus } from '../types'
import { ManualRows, PercentCell, Total, type RowsTableProps } from './ApplicationRowsTable'

const ROW_H = 32 // h-8
const HEADER_H = 40 // h-10
const FOOTER_H = 36 // h-9
/** Name indentation by tree depth 0–3. */
const INDENT = ['pl-1', 'pl-5', 'pl-9', 'pl-[3.25rem]']
const NAME = 'w-[22rem]'
const W = { value: 'w-44', previous: 'w-44', requested: 'w-44', percent: 'w-28', certified: 'w-52', note: 'w-48', remaining: 'w-44' } as const
const COLUMNS = [
  ['value', 'CostControl_application_col_value'], ['previous', 'CostControl_application_col_previous'], ['requested', 'CostControl_application_col_requested'],
  ['percent', 'CostControl_application_col_percent'], ['certified', 'CostControl_application_col_certified'], ['note', 'CostControl_application_col_note'],
  ['remaining', 'CostControl_application_col_remaining'],
] as const
const CELL = 'flex h-full shrink-0 items-center px-2 group-hover:bg-accent'
const MONEY = cn(CELL, 'justify-end whitespace-nowrap tabular-nums')
const indent = (depth: number) => INDENT[Math.min(depth, INDENT.length - 1)]
const pct = (value: string, previous: string, amount: string, locale: string) => `${toEditText(percentDone(value, previous, amount), locale)} %`

interface GroupProps {
  id: string; depth: number; name: string; code: string | null; value: string; previous: string; current: string; counted: string
  top: number; collapsed: boolean; showReview: boolean; currency: string; locale: string; onToggle: (id: string) => void
}

/**
 * A budget group with the subtotals of its visible rows; its % is on what they bill (`counted`), as each row's is.
 * Plain props, so only the groups whose sums change re-render.
 */
const Group = memo(({ id, depth, name, code, value, previous, current, counted, top, collapsed, showReview, currency, locale, onToggle }: GroupProps) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  const bg = depth === 0 ? 'bg-muted' : 'bg-background'
  const label = code ? `${code} · ${name}` : name
  return (
    <div
      role="row"
      aria-level={depth + 1}
      aria-expanded={!collapsed}
      className={cn('group absolute left-0 flex h-8 min-w-max border-b text-sm', bg, depth === 0 ? 'font-semibold' : 'font-medium')}
      // ui-drift: dynamic
      style={{ top }}
    >
      <div role="gridcell" className={cn('sticky left-0 z-10 gap-1 border-r pr-1', CELL, bg, NAME, indent(depth))}>
        <button
          type="button"
          className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={t(collapsed ? 'CostControl_expand' : 'CostControl_collapse')}
          onClick={() => onToggle(id)}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <span className="min-w-0 flex-1 truncate" title={label}>{label}</span>
      </div>
      <div role="gridcell" className={cn(MONEY, W.value)}>{money(value)}</div>
      <div role="gridcell" className={cn(MONEY, W.previous)}>{money(previous)}</div>
      <div role="gridcell" className={cn(MONEY, W.requested)}>{money(current)}</div>
      <div role="gridcell" className={cn(MONEY, W.percent)}>{pct(value, previous, counted, locale)}</div>
      {showReview && <div role="gridcell" className={cn(CELL, W.certified)} />}
      {showReview && <div role="gridcell" className={cn(CELL, W.note)} />}
      <div role="gridcell" className={cn(CELL, W.remaining)} />
    </div>
  )
})
Group.displayName = 'InvestorGroup'

interface LineProps {
  row: CostApplicationRow; depth: number; top: number
  /** This row's own draft and review entries: an edit of another row leaves this one alone. */
  entry: DraftAmount | undefined; stored: DraftReview | undefined
  mode: RowsMode; status: CostApplicationStatus; showReview: boolean; disabled: boolean; currency: string; locale: string
  onDraft: RowsTableProps['onDraft']; onReview: RowsTableProps['onReview']
}

/** One item row with the cells of ApplicationRowsTable on a single 32 px line. A manual row (view only) shows its text. */
const Line = memo(({ row: r, depth, top, entry, stored, mode, status, showReview, disabled, currency, locale, onDraft, onReview }: LineProps) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  // One-key maps: the row receives only its own entries, which keeps the memo intact.
  const typed = draftRequested(r, entry ? { [r.key]: entry } : {})
  const requested = typed ?? '0.00'
  const rv = reviewOf(r, stored ? { [r.key]: stored } : {})
  const { certified, counted, lowered, remaining, reviewable } = rowFigures(r, requested, rv, mode, status)
  const manual = r.kind === 'MANUAL'
  const item = r.code ? `${r.code} · ${r.name}` : r.name
  const label = manual ? r.description ?? '' : item
  const dash = <span className="text-muted-foreground">—</span>
  return (
    <div
      role="row"
      aria-level={depth + 1}
      className="group absolute left-0 flex h-8 min-w-max border-b bg-background text-sm"
      // ui-drift: dynamic
      style={{ top }}
    >
      <div role="gridcell" className={cn('sticky left-0 z-10 gap-1 border-r bg-background pr-1', CELL, NAME, indent(depth))}>
        <span className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate" title={label}>{label}</span>
        {manual && r.nodeId && <span className="min-w-0 truncate text-xs text-muted-foreground" title={item}>{item}</span>}
      </div>
      <div role="gridcell" className={cn(MONEY, W.value)}>{manual ? dash : money(r.value)}</div>
      <div role="gridcell" className={cn(MONEY, W.previous, 'text-muted-foreground')} title={manual ? undefined : pct(r.value, '0', r.previous, locale)}>{manual ? dash : money(r.previous)}</div>
      <div role="gridcell" className={cn(MONEY, W.requested)}>
        {mode === 'draft'
          ? <MoneyInput value={typed} onChange={(value, valid) => onDraft(r.key, value, valid)} disabled={disabled} className="h-7" aria-label={t('CostControl_application_col_requested')} />
          : money(requested)}
      </div>
      <div role="gridcell" className={cn(MONEY, W.percent)}>
        {manual ? dash : mode === 'draft'
          ? <PercentCell value={percentDone(r.value, r.previous, requested)} disabled={disabled} label={t('CostControl_application_col_percent')} className="h-7" onCommit={(p) => onDraft(r.key, requestedFromPercent(r.value, r.previous, p), true)} />
          : pct(r.value, r.previous, counted, locale)}
      </div>
      {showReview && (
        <div role="gridcell" className={cn(MONEY, W.certified, 'gap-1')}>
          {reviewable
            ? <MoneyInput value={rv.certified} onChange={(value, valid) => onReview(r, { certified: value, valid })} disabled={disabled} className="h-7 min-w-0 flex-1" aria-label={t('CostControl_application_col_certified')} />
            : certified === null ? dash : money(certified)}
          {lowered && <Badge variant="warning" className="shrink-0">{t('CostControl_application_lowered')}</Badge>}
        </div>
      )}
      {showReview && (
        <div role="gridcell" className={cn(CELL, W.note)}>
          {reviewable
            ? <Input value={rv.note} onChange={(e) => onReview(r, { note: e.target.value })} disabled={disabled} className="h-7" aria-label={t('CostControl_application_col_note')} />
            : <span className="truncate" title={r.note ?? undefined}>{r.note ?? ''}</span>}
        </div>
      )}
      <div role="gridcell" className={cn(MONEY, W.remaining, !manual && compareMoneyStrings(remaining, '0') < 0 && 'text-destructive')}>{manual ? dash : money(remaining)}</div>
    </div>
  )
})
Line.displayName = 'InvestorLine'

/**
 * The rows of a main-contract application (spec §2.3): grouped by the budget tree with subtotals, a search, "only rows
 * with an amount", virtualized as BudgetTreeGrid (one scroller, sticky header, footer and name column, fixed 32 px rows).
 * Manual rows and the application totals follow below the grid.
 */
const InvestorRowsTable: React.FC<RowsTableProps> = ({ rows, hasRows, mode, status, showReview, draft, review, totals, retentionPercent, disabled, currency, locale, onDraft, onReview, manual, allowedNodeIds, tree, onManual }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [onlyWithAmount, setOnlyWithAmount] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const amountOf = useCallback((r: CostApplicationRow) => draftRequested(r, draft) ?? '0.00', [draft])
  const countedOf = useCallback((r: CostApplicationRow) => rowFigures(r, amountOf(r), reviewOf(r, review), mode, status).counted, [amountOf, review, mode, status])
  const items = useMemo(() => investorTableRows(tree, rows, amountOf, { collapsed, search, onlyWithAmount }, countedOf), [tree, rows, amountOf, collapsed, search, onlyWithAmount, countedOf])
  // A search opens every group (investorTableRows), so no chevron may claim otherwise.
  const searching = !!normalizeSearch(search)
  // The footer covers every row, as the group subtotals do: the search and filter only choose what is listed.
  const sums = useMemo(() => {
    const of = (pick: (r: CostApplicationRow) => string) => sumMoneyStrings(rows.map(pick))
    return { value: of((r) => r.value), previous: of((r) => r.previous), current: of(amountOf), counted: of(countedOf) }
  }, [rows, amountOf, countedOf])
  const onToggle = useCallback((id: string) => setCollapsed((c) => {
    const next = new Set(c)
    if (!next.delete(id)) next.add(id)
    return next
  }), [])
  const scrollerRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
    scrollMargin: HEADER_H,
    scrollPaddingStart: HEADER_H,
    scrollPaddingEnd: FOOTER_H,
  })
  const money = (v: string) => formatMoney(v, { locale, currency })
  if (!rows.length && mode === 'view') {
    return <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{t(hasRows ? 'CostControl_application_rows_no_match' : 'CostControl_application_rows_empty')}</p>
  }
  const columns = COLUMNS.filter(([id]) => showReview || (id !== 'certified' && id !== 'note'))
  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setCollapsed(new Set(collapsibleIds(tree)))} aria-label={t('CostControl_collapse_all')} title={t('CostControl_collapse_all')}>
            <ChevronsDownUp className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setCollapsed(new Set())} aria-label={t('CostControl_expand_all')} title={t('CostControl_expand_all')}>
            <ChevronsUpDown className="h-4 w-4" />
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <Switch id="cc-investor-billed" checked={onlyWithAmount} onCheckedChange={setOnlyWithAmount} />
            <Label htmlFor="cc-investor-billed" className="text-sm">{t('CostControl_application_only_billed')}</Label>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t('CostControl_search_placeholder')} aria-label={t('Common_search')} value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-[220px] pl-8 text-sm" />
          </div>
        </div>
      )}
      {items.length ? (
        // The scroll-padding classes keep a focused input clear of the sticky header, footer and name column (BudgetTreeGrid).
        <div ref={scrollerRef} role="treegrid" aria-label={t('CostControl_application_rows_title')} aria-rowcount={items.length} className="relative max-h-[70vh] scroll-pt-10 scroll-pb-9 scroll-pl-[22rem] overflow-auto rounded-md border [&::-webkit-scrollbar-corner]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar]:size-2.5">
          <div role="row" className="sticky top-0 z-30 flex h-10 min-w-max border-b bg-muted text-xs font-medium text-muted-foreground">
            <div role="columnheader" className={cn('sticky left-0 z-40 flex shrink-0 items-center border-r bg-muted px-2', NAME)}>{t('CostControl_application_col_item')}</div>
            {columns.map(([id, label]) => (
              <div key={id} role="columnheader" className={cn('flex shrink-0 items-center px-2 leading-tight', W[id], id !== 'note' && 'justify-end text-right')}>{t(label)}</div>
            ))}
          </div>
          <div
            className="relative min-w-max"
            // ui-drift: dynamic
            style={{ height: items.length * ROW_H }}
          >
            {virtualizer.getVirtualItems().map((vi) => {
              const it = items[vi.index]
              const top = vi.index * ROW_H
              return it.kind === 'group' ? (
                <Group
                  key={`g:${it.id}`}
                  id={it.id} depth={it.depth} name={it.name} code={it.code} value={it.value} previous={it.previous} current={it.current} counted={it.counted}
                  top={top} collapsed={collapsed.has(it.id) && !searching} showReview={showReview} currency={currency} locale={locale} onToggle={onToggle}
                />
              ) : (
                <Line
                  key={it.row.key}
                  row={it.row} depth={it.depth} top={top} entry={draft[it.row.key]} stored={review[it.row.key]}
                  mode={mode} status={status} showReview={showReview} disabled={disabled} currency={currency} locale={locale} onDraft={onDraft} onReview={onReview}
                />
              )
            })}
          </div>
          <div role="row" className="sticky bottom-0 z-30 flex h-9 min-w-max border-t bg-muted text-sm font-semibold">
            <div role="gridcell" className={cn('sticky left-0 z-40 flex shrink-0 items-center border-r bg-muted px-2', NAME)}>{t('Common_total')}</div>
            <div role="gridcell" className={cn(MONEY, W.value)}>{money(sums.value)}</div>
            <div role="gridcell" className={cn(MONEY, W.previous)}>{money(sums.previous)}</div>
            <div role="gridcell" className={cn(MONEY, W.requested)}>{money(sums.current)}</div>
            <div role="gridcell" className={cn(MONEY, W.percent)}>{pct(sums.value, sums.previous, sums.counted, locale)}</div>
            {showReview && <div role="gridcell" className={cn(CELL, W.certified)} />}
            {showReview && <div role="gridcell" className={cn(CELL, W.note)} />}
            <div role="gridcell" className={cn(CELL, W.remaining)} />
          </div>
        </div>
      ) : (
        <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{t(rows.length ? 'CostControl_application_rows_no_match' : 'CostControl_application_rows_empty')}</p>
      )}
      <div className="rounded-md border">
        <Table>
          {mode !== 'view' && (
            <TableBody>
              <ManualRows mode={mode} manual={manual} showReview={showReview} disabled={disabled} columns={columns.length + 1} tree={tree} allowedNodeIds={allowedNodeIds} money={money} onManual={onManual} />
            </TableBody>
          )}
          <TableFooter className="bg-muted">
            <Total columns={columns.length + 1} money={money} label={t('CostControl_application_total_requested')} value={totals.requested} />
            {status !== 'DRAFT' && <Total columns={columns.length + 1} money={money} label={t('CostControl_application_total_certified')} value={totals.certified} strong />}
            {retentionPercent !== null && <Total columns={columns.length + 1} money={money} label={t('CostControl_invoice_field_retention', { percent: toEditText(retentionPercent, locale) })} value={totals.retention} />}
            <Total columns={columns.length + 1} money={money} label={t('CostControl_invoice_field_payable')} value={totals.payable} strong />
          </TableFooter>
        </Table>
      </div>
    </div>
  )
}
export default InvestorRowsTable
